// Resolves a lead's own negotiated Sakthi Power tariff — the "Sakthi PF
// Charge" set on its Proposal — so that value can be threaded through
// Mapping → Allocation → Invoice once a deal closes, instead of those stages
// each re-deriving a tariff from Tariff Master independently (which may not
// reflect what was actually agreed with this specific lead). Callers should
// use this FIRST and fall back to a Tariff Master lookup only when a lead
// has no closed proposal of its own (e.g. legacy records created before a
// proposal existed).
import { leadStore } from '@/pages/Lead/leadStore'
import { proposalStore } from '@/pages/Proposal/proposalStore'
import { ProposalStatus } from '@/types'
import { isSameCompanyName } from './companyMatch'

// Same "best proposal for a lead" preference (Won, then Approved) used by
// useLeadCompanies.ts's Rule §14 gate — a lead is only "closed" once one of
// its proposals has actually been won or approved.
function bestClosedProposalForLead(leadId: string) {
  const candidates = proposalStore.getAll().filter(p => p.leadId === leadId)
  return (
    candidates.find(p => p.status === ProposalStatus.WON) ||
    candidates.find(p => p.status === ProposalStatus.APPROVED) ||
    undefined
  )
}

// Generator/Consumer master records have no direct id link back to the lead
// they originated from — matched by company name instead, same as
// ProposalForm.tsx already does when auto-filling a proposal from a lead.
export function resolveLeadProposalTariff(
  leadType: 'Generator' | 'Consumer',
  companyName: string
): number | null {
  if (!companyName?.trim()) return null
  const lead = leadStore.getLeads().find(
    l => l.leadType === leadType && isSameCompanyName(l.companyName || '', companyName)
  )
  if (!lead) return null
  const proposal = bestClosedProposalForLead(lead.id)
  const rate = proposal?.powerFacilitationCharge
  return typeof rate === 'number' && rate > 0 ? rate : null
}
